import { Api } from '@automapa/svelte-automapa-template';
import { z } from 'zod';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';

export const WarehouseRequestSchema = z.object({
    name: z.string().catch(''),
    geom: z.object({
        type: z.string().regex(/^Point$/),
        coordinates: z.array(z.number()),
    }),
    details: z
        .object({
            query: z.string().catch(''),
            city: z.string().catch(''),
            street: z.string().catch(''),
            house: z.string().catch(''),
            abbreviation: z.string().catch(''),
        })
        .catch({
            query: '',
            city: '',
            street: '',
            house: '',
            abbreviation: '',
        }),
});
export type WarehouseRequestType = z.infer<typeof WarehouseRequestSchema>;

export const WarehouseResponseSchema = WarehouseRequestSchema.merge(
    z.object({
        id: z.number(),
    })
);
export type WarehouseResponseType = z.infer<typeof WarehouseResponseSchema>;

export class WarehouseApi {
    public static async getAll(api: Api): Promise<WarehouseResponseType[]> {
        return api.getData(
            'get',
            '/warehouses',
            undefined,
            z.undefined(),
            z.array(WarehouseResponseSchema)
        );
    }

    public static async getOne(api: Api, warehouseId: number): Promise<WarehouseResponseType> {
        return api.getData(
            'get',
            `/warehouse/${warehouseId}`,
            undefined,
            z.undefined(),
            WarehouseResponseSchema
        );
    }

    public static async createOne(
        api: Api,
        warehouse: WarehouseRequestType
    ): Promise<WarehouseResponseType> {
        return api.getData(
            'post',
            '/warehouse',
            warehouse,
            WarehouseRequestSchema,
            WarehouseResponseSchema
        );
    }

    public static async updateOne(
        api: Api,
        warehouseId: number,
        warehouse: WarehouseRequestType
    ): Promise<WarehouseResponseType> {
        return api.getData(
            'put',
            `/warehouse/${warehouseId}`,
            warehouse,
            WarehouseRequestSchema,
            WarehouseResponseSchema
        );
    }

    public static async addOne(
        api: Api,
        warehouse: WarehouseRequestType
    ): Promise<WarehouseResponseType> {
        return api.getData(
            'post',
            '/warehouse',
            warehouse,
            WarehouseRequestSchema,
            WarehouseResponseSchema
        );
    }

    public static async removeOne(api: Api, warehouseId: number): Promise<any> {
        return api.delete(`/warehouse/${warehouseId}`);
    }

    public static async patch(
        api: Api,
        warehouseId: number,
        path: string,
        value: unknown
    ): Promise<WarehouseResponseType> {
        return api.patch(
            `/warehouse/${warehouseId}`,
            'replace',
            path,
            value,
            WarehouseResponseSchema
        );
    }

    public static async filter(api: Api, f: ApiFilterBuilder): Promise<WarehouseResponseType[]> {
        return api.getData(
            'get',
            `/warehouse/` + f.getUri(),
            undefined,
            z.undefined(),
            z.array(WarehouseResponseSchema)
        );
    }

    public static async getCountAll(api: Api): Promise<WarehouseResponseType[]> {
        return api.getData(
            'get',
            '/warehouses/count',
            undefined,
            z.undefined(),
            z.object({count: z.coerce.number().catch(0)})
        );
    }
}
